import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { BaseLinkContextProvider } from "shared/components/common/base-link/context";
import { useTrackImpressionEventByIntersection } from "shared/hooks";

import { PlusArchiveTitle } from "scmp-app/components/plus/archive/archive-title";
import { usePlusArticlePopup } from "scmp-app/components/plus/article-popup/hooks";
import { usePlusDetailedListingPopup } from "scmp-app/components/plus/detailed-listing-popup/hooks";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { RecirculationWidgetImpressionEvent } from "scmp-app/components/tracking/google-analytics-4/types";
import type { betweenTheLinesPlusArchiveQuery$key } from "scmp-app/queries/__generated__/betweenTheLinesPlusArchiveQuery.graphql";

import { Container, ContentItemContainer, StyledContentItemPlusPrimary } from "./styles";

type Props = {
  className?: string;
  reference: betweenTheLinesPlusArchiveQuery$key;
};
export const PlusArchiveBetweenTheLines: FunctionComponent<Props> = ({ className, reference }) => {
  const content = useFragment(
    graphql`
      fragment betweenTheLinesPlusArchiveQuery on Query
      @argumentDefinitions(betweenTheLinesTypeUuid: { type: "String!" }) {
        plusArchiveBetweenTheLines: articles(
          filter: { typeIds: [$betweenTheLinesTypeUuid] }
          first: 2
          orderBy: { field: PUBLISHED_DATE, direction: DESC }
        ) {
          edges {
            node {
              entityId
              ...plusPrimaryContentItemContent
              ...hooksPlusArticleLinkHandlerArticle
            }
          }
        }
      }
    `,
    reference,
  );

  const { handleOpen: handleOpenDetailedListingPopup } = usePlusDetailedListingPopup();
  const { handleOpen: handleOpenArticlePopup } = usePlusArticlePopup();

  const { captureTrackImpressionEventTargetElement } =
    useTrackImpressionEventByIntersection<RecirculationWidgetImpressionEvent>({
      ga4TrackingHandler: sendGA4Tracking,
      getGa4Event: () => ({
        action: "imp",
        category: "recirculation",
        customized_parameters: {
          list_of_articles: (
            content?.plusArchiveBetweenTheLines?.edges.map(edge => edge.node.entityId) ?? []
          ).join(","),
          page_type: "archive",
          widget_name: "between_the_lines",
        },
        subcategory: "widget",
      }),
      options: {
        isSendGA4Tracking: true,
        shouldSendOnce: true,
      },
    });

  if (!content?.plusArchiveBetweenTheLines?.edges?.length) return null;

  return (
    <BaseLinkContextProvider
      customQueryParameters={{
        module: "between_the_lines",
      }}
    >
      <Container className={className} ref={captureTrackImpressionEventTargetElement}>
        <PlusArchiveTitle
          onClick={() =>
            handleOpenDetailedListingPopup({
              pgtype: "archive",
              type: "between-the-lines",
            })
          }
          title="Between the Lines"
        />
        <ContentItemContainer>
          {content?.plusArchiveBetweenTheLines?.edges?.map(edge => (
            <StyledContentItemPlusPrimary
              key={edge.node.entityId}
              onClick={() =>
                handleOpenArticlePopup(edge.node, {
                  pageType: "archive",
                  widgetName: "between_the_lines",
                })
              }
              reference={edge.node}
              withArticleType={false}
              withSummary={false}
            />
          ))}
        </ContentItemContainer>
      </Container>
    </BaseLinkContextProvider>
  );
};

PlusArchiveBetweenTheLines.displayName = "PlusArchiveBetweenTheLines";
